import { inject } from '@angular/core';
import type { Routes } from '@angular/router';

import { requireApp, requirePermission } from '@core/access/access.guard';
import { authChildGuard, authGuard } from '@core/auth/auth.guard';
import { guestGuard } from '@core/auth/guest.guard';
import { ProjectNavigationService } from '@core/projects/project-navigation.service';
import { PROJECTS_PATH, provideActiveProjectFromRoute } from '@core/projects/project-scope';

const loadTaskerRoutes = (): Promise<Routes> =>
  import('@features/tasker/tasker.routes').then((m) => m.TASKER_ROUTES);
const loadSchedulerRoutes = (): Promise<Routes> =>
  import('@features/scheduler/scheduler.routes').then((m) => m.SCHEDULER_ROUTES);

export const APP_ROUTES: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('@layouts/auth-layout/auth-layout.component').then((m) => m.AuthLayoutComponent),
    canActivate: [guestGuard],
    loadChildren: () => import('@features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authChildGuard],
    loadComponent: () =>
      import('@layouts/application-layout/application-layout.component').then(
        (m) => m.ApplicationLayoutComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadChildren: () =>
          import('@features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
      },
      {
        path: 'dashboard',
        redirectTo: '',
        pathMatch: 'full',
      },
      {
        path: 'scheduler',
        loadChildren: loadSchedulerRoutes,
      },
      {
        path: 'users',
        canActivate: [requirePermission('users:read')],
        loadChildren: () => import('@features/users/users.routes').then((m) => m.USERS_ROUTES),
      },
      {
        path: 'recruiting',
        // An app-market application: without it the backend refuses these
        // routes too. The public join and invite pages below stay open.
        canActivate: [requireApp('recruiting')],
        loadChildren: () =>
          import('@features/recruiting/recruiting.routes').then((m) => m.RECRUITING_ROUTES),
      },
      {
        path: 'notification-templates',
        loadChildren: () =>
          import('@features/notification-templates/notification-templates.routes').then(
            (m) => m.NOTIFICATION_TEMPLATES_ROUTES,
          ),
      },
      {
        path: 'tasker',
        loadChildren: loadTaskerRoutes,
      },
      {
        // Project mode: the same feature routes (and guards), scoped to the
        // project in the URL. Apps listed in PROJECT_APPS each mount here.
        // Listing projects is employer-only, like the Projects entry.
        path: 'projects',
        canActivate: [requirePermission('settings:manage')],
        children: [
          {
            path: '',
            pathMatch: 'full',
            title: 'nav.projects',
            loadComponent: () =>
              import('@features/projects/pages/projects-home/projects-home.component').then(
                (m) => m.ProjectsHomeComponent,
              ),
          },
          {
            path: ':projectId',
            providers: [provideActiveProjectFromRoute()],
            loadComponent: () =>
              import('@features/projects/pages/project-scope/project-scope.component').then(
                (m) => m.ProjectScopeComponent,
              ),
            children: [
              {
                path: '',
                pathMatch: 'full',
                // The first app page this session may open in the project.
                redirectTo: ({ params }) =>
                  inject(ProjectNavigationService).firstLink(String(params['projectId'] ?? '')) ??
                  PROJECTS_PATH,
              },
              { path: 'tasker', loadChildren: loadTaskerRoutes },
              { path: 'scheduler', loadChildren: loadSchedulerRoutes },
            ],
          },
        ],
      },
      {
        path: 'learning',
        loadChildren: () =>
          import('@features/learner/learner.routes').then((m) => m.LEARNER_ROUTES),
      },
      {
        path: 'payments',
        loadChildren: () =>
          import('@features/dashboard/payments.routes').then((m) => m.PAYMENTS_ROUTES),
      },
      {
        path: 'payroll',
        loadChildren: () =>
          import('@features/payroll/payroll.routes').then((m) => m.PAYROLL_ROUTES),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('@features/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
      },
      {
        path: 'my-apps/pop',
        title: 'payroll.config.title',
        canActivate: [
          requireApp('payments'),
          requireApp('pop'),
          requirePermission('payments:manage'),
        ],
        loadComponent: () =>
          import('@features/payroll/pages/pop-config/pop-config.component').then(
            (m) => m.PopConfigComponent,
          ),
      },
      {
        path: 'my-apps',
        loadChildren: () =>
          import('@features/my-apps/my-apps.routes').then((m) => m.MY_APPS_ROUTES),
      },
      {
        path: 'company',
        loadChildren: () =>
          import('@features/company/company.routes').then((m) => m.COMPANY_ROUTES),
      },
    ],
  },
  {
    // Celeri's job board: every listed, open campaign. No sign-in guard:
    // anyone reads it, and a card opens the job page below.
    path: 'jobs',
    title: 'rc.board.title',
    loadComponent: () =>
      import('@features/recruiting/pages/board/board.component').then((m) => m.BoardComponent),
  },
  {
    // What a recruiting link opens. No sign-in guard: anyone sees the
    // campaign, and the page sends people to sign in to apply.
    path: 'join/:code',
    title: 'rc.join.title',
    loadComponent: () =>
      import('@features/recruiting/pages/join/join.component').then((m) => m.JoinComponent),
  },
  {
    // An invite back to last season's crew. No sign-in: the link is
    // personal.
    path: 'invite/:token',
    title: 'rc.invite.pageTitle',
    loadComponent: () =>
      import('@features/recruiting/pages/invite/invite.component').then((m) => m.InviteComponent),
  },
  {
    path: '**',
    loadComponent: () =>
      import('@features/errors/pages/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
  },
];
